// RUTA: src/components/fitness/RestTimerPill.tsx
//
// Pill flotante que aparece arriba-centro cuando hay un timer
// activo y el modal está minimizado. Permite al cliente seguir
// navegando la rutina sin perder el cronómetro.
//
// Tap en el pill → abre el modal full-screen del timer.
// Color cambia según el estado:
//   - Corriendo: accent (rojo IFT)
//   - Últimos 10s: destructive (advertencia)
//   - Alarma: pulse rojo
//
// Animación slide-in desde arriba la primera vez que aparece —
// patrón implícito de enseñanza ("el timer se mudó aquí, sigue
// corriendo").

import { Timer, Pause, Play } from 'lucide-react';
import { useRestTimer, formatTime } from '@/contexts/RestTimerContext';

export function RestTimerPill() {
  const t = useRestTimer();

  // Solo aparecer cuando hay timer activo Y el modal está cerrado
  if (!t.active || t.modalOpen) return null;

  const isLast10 = t.remaining <= 10 && t.remaining > 0 && t.started;
  const isFinished = t.alarming;

  // Color del pill según estado
  const bgClass = isFinished
    ? 'bg-destructive animate-pulse shadow-destructive/50'
    : isLast10
      ? 'bg-destructive shadow-destructive/40'
      : 'bg-accent shadow-accent/40';

  return (
    <div
      // Posicionado en BOTTOM-LEFT. Razones:
      //
      //   - Top estaba colisionando con las pestañas de días de la
      //     rutina y los sticky indicators (cada sección tiene su
      //     propio header, ningún offset top funciona para todas).
      //
      //   - Bottom-right tiene SupportFab (80px desde bottom) y
      //     ConnectionIndicator (16px). Bottom-center también podría
      //     chocar con ConnectionIndicator en pantallas chicas.
      //
      //   - Bottom-LEFT está completamente libre. Tap target accesible
      //     para pulgares. Patrón usado por Trainerize.
      className="fixed z-40"
      style={{
        left: 'max(env(safe-area-inset-left, 0px), 0.75rem)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)',
        maxWidth: 'calc(100vw - 1.5rem)',
      }}
      role="status"
      aria-live="polite"
    >
      <button
        onClick={() => t.openModal({})}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-full ${bgClass} text-white shadow-2xl animate-stagger-fade-up active:scale-95 transition-transform max-w-[calc(100vw-1.5rem)]`}
        aria-label="Abrir cronómetro de descanso"
      >
        <Timer className="w-4 h-4 shrink-0" />

        <div className="flex flex-col items-start min-w-0">
          {t.exerciseName && (
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-80 truncate max-w-[140px] leading-tight">
              {t.exerciseName}
            </span>
          )}
          <span className="text-base font-black tabular-nums leading-tight">
            {isFinished ? '¡Listo!' : formatTime(t.remaining)}
          </span>
        </div>

        {/* Botón de pause/play inline. stopPropagation para no abrir el modal. */}
        {t.started && !isFinished && (
          <span
            onClick={(e) => { e.stopPropagation(); t.togglePause(); }}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 hover:bg-white/30 transition-colors"
            role="button"
            aria-label={t.running ? 'Pausar' : 'Reanudar'}
          >
            {t.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </span>
        )}

        {isFinished && (
          <span
            onClick={(e) => { e.stopPropagation(); t.stopAlarm(); }}
            className="px-2 py-1 rounded-full bg-white/20 hover:bg-white/30 text-[10px] font-black uppercase tracking-wider shrink-0"
            role="button"
            aria-label="Detener alarma"
          >
            OK
          </span>
        )}
      </button>
    </div>
  );
}
